import React from "react";
import { useDispatch } from "react-redux";

import { showDetails, updateSearchQuery } from "@dashboard/devices/store/index";
import { Device, EventReport, Group, License } from "@dashboard/devices/types";

type UseDeviceNameClickHandlerProps = {
    devices: (Device | null)[];
    licensesEntities: Record<number, License>;
    allGroups?: Group[];
    groupId?: number;
};

export const useDeviceNameClickHandler = (options: UseDeviceNameClickHandlerProps) => {
    const dispatch = useDispatch();

    return React.useCallback(
        (event: EventReport) => {
            const isDeviceEvent = event.eventTypeName.startsWith("DeviceEvent.");
            const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");

            if (isDeviceEvent) {
                const clickedDevice = options.groupId
                    ? options.devices?.find((d) => d?.id.toString() === event.entityId)
                    : options.allGroups
                          ?.flatMap((g) => g.licenseIds)
                          .map((licenseId) => options.licensesEntities[licenseId]?.device)
                          .filter((d): d is Device => d !== null)
                          .find((d) => d?.id.toString() === event.entityId);

                if (clickedDevice?.licenseId && clickedDevice.name) {
                    const groupId =
                        options.groupId || options.allGroups?.find((g) => g.licenseIds.includes(clickedDevice.licenseId))?.id;

                    if (groupId) {
                        dispatch(updateSearchQuery(clickedDevice.serial || ""));
                        dispatch(showDetails({ type: "license", selectedId: clickedDevice.licenseId }));
                    }
                }
            } else if (isLicenseEvent) {
                const license = options.licensesEntities[parseInt(event.entityId)];
                if (!license) {
                    return;
                }

                const device =
                    license.device || (options.groupId ? options.devices?.find((d) => d?.licenseId === license.id) : undefined);

                if (device) {
                    const groupId = options.groupId || options.allGroups?.find((g) => g.licenseIds.includes(license.id))?.id;

                    if (groupId) {
                        dispatch(updateSearchQuery(device.serial || ""));
                        dispatch(showDetails({ type: "license", selectedId: device.licenseId }));
                    }
                }
            }
        },
        [dispatch, options.devices, options.allGroups, options.licensesEntities, options.groupId],
    );
};
